import * as React from 'react';
import type { ITextField } from '@fluentui/react/lib/TextField';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { getTodoFolderById, getTodoFoldersLoadState } from 'owa-todo-folders';
import {
    usePatchPartialPayload,
    useTextChangeCallback,
    useProjectId,
} from 'owa-timestream-asset-sdk';
import { SPACES_ACTION_SOURCE, updateTodoSubject } from 'owa-timestream-asset-sdk-tasks';
import useCreateTask from '../hooks/useCreateTask';
import type { TaskViewPayload, TaskViewTitleProps } from '../schema';
import { useOnBlurUpdate } from 'owa-react-hooks/lib/useOnBlurUpdate';

export default function useTaskViewTitleState({
    taskId,
    isComplete,
    folderId,
    taskSubject,
    hasLocalSubject,
    newSubtaskRef,
}: TaskViewTitleProps) {
    const taskFolder = getTodoFolderById(folderId);
    const folderLoadState = getTodoFoldersLoadState();
    const patchPartialPayload = usePatchPartialPayload<TaskViewPayload>();
    const createTask = useCreateTask();
    const projectId = useProjectId();
    const taskSubjectFieldRef = React.useRef<ITextField>(null);
    const [newTaskSubject, setTaskSubject] = React.useState(taskSubject!);

    // if there is a pre-existing task, update it's subject when that info is given
    React.useEffect(() => {
        setTaskSubject(taskSubject!);
        hasLocalSubject(taskSubject);
    }, [taskSubject]);

    const onTextFieldChange = useTextChangeCallback((subject: string) => {
        setTaskSubject(subject);
        hasLocalSubject(subject);
    });

    const onTextFieldBlur = React.useCallback(() => {
        if (!taskId && !!newTaskSubject) {
            // focus on the add subtask field if its a new task
            newSubtaskRef?.current?.focus();
        }

        if (!!newTaskSubject) {
            if (!taskId) {
                createTask(folderId, newTaskSubject);
            } else {
                if (newTaskSubject !== taskSubject) {
                    updateTodoSubject(taskId, newTaskSubject, {
                        actionSource: SPACES_ACTION_SOURCE,
                        viewType: 'Item',
                    });
                    patchPartialPayload({ taskSubject: newTaskSubject });
                }
            }
        } else {
            // return the subject to the old subject if the new subject is empty
            setTaskSubject(taskSubject!);
        }
    }, [
        taskId,
        newTaskSubject,
        taskSubject,
        isComplete,
        projectId,
        folderId,
        taskFolder,
        folderLoadState,
    ]);

    const onBlur = useOnBlurUpdate(onTextFieldBlur);

    const onKeyDown = React.useCallback(
        (ev: React.KeyboardEvent) => {
            switch (ev.keyCode) {
                case KeyCodes.enter:
                    ev.preventDefault();
                    taskSubjectFieldRef?.current?.blur();
                    break;
                case KeyCodes.escape:
                    setTaskSubject(taskSubject!);
                    break;
            }
        },
        [taskSubjectFieldRef, taskSubject]
    );

    return {
        isComplete: isComplete,
        textFieldProps: {
            componentRef: taskSubjectFieldRef,
            onBlur: onBlur,
            onChange: onTextFieldChange,
            onKeyDown: onKeyDown,
            value: newTaskSubject,
        },
    };
}
